
import { API } from 'src/apis/apiWrapper';
import { IAIRecord } from "src/types/org/ai";
import { IDIRecord } from "src/types/org/di";
import { IOrgRecord } from 'src/types/org/orgInterface';
import { processResponse } from 'src/utils/api';
import { IMsReferenceService } from './msReference';

export interface IOrgService {
  search: (params: ISearchOptions) => Promise<IOrgSearch[]>;
  searchAis: (params: ISearchOptions) => Promise<IAISearch[]>;
  searchDis: (params: ISearchOptions) => Promise<IDISearch[]>;
  findAi: (aiCode: string | undefined, orgId: string | undefined) => Promise<IAIRecord>;
  findDi: (orgId: string | undefined) => Promise<IDIRecord>; //findDi internally gets the associaated diCode for that orgId
  findOrgById: (orgId: string) => Promise<IOrgRecord>;
}

export interface ISearchOptions {
  zip?: string;
  distance?: string;
  state?: string;
  intl_postal_cd?: string;
  iso?: string;
  name?: string;
  aicode?: string;
  dicode?: string;
  text?: string;
  aicategory?: string;
  aistatus?: string;
  address?: string;
  city?: string;
  orgid?: string;
  orgtype?: string;
  orgsubtype?: string;
  size?: string;
  province?: string;
}

export interface ISourceAi {
  ai_category_cd: '1' | '2';
  ai_code: string;
  ai_level_cd: '1' | '2';
  ai_source_cd: string;
  ai_status_cd: '1' | '2';
  ap_eligibility_ind: 'Y' | 'N';
  psat_eligibility_ind: 'Y' | 'N';
  sat_eligibility_ind: 'Y' | 'N';
  st_home_sch_rptg_ind: 'Y' | 'N';
  clep_eligibility_ind: 'Y' | 'N';
  ssd_eligibility_ind: 'Y' | 'N';
}

// CLEP program logic is currently not needed in DI Functionality. Informed to comment it out!
export interface ISourceDi {
  di_code: string;
  ap_pgm_active_ind: 'Y' | 'N';
  sat_pgm_active_ind: 'Y' | 'N';
  //clep_pgm_active_ind: 'Y' | 'N';
  sat_contact_nm: string;
  sat_contact_title: string;
  sat_contact_email: string;
  sat_contact_phone: string;
  ap_contact_nm: string;
  ap_contact_title: string;
  ap_contact_email: string;
  ap_contact_phone: string;
  // clep_contact_nm: string;
  // clep_contact_title: string;
  // clep_contact_email: string;
  // clep_contact_phone: string;
}

export interface ISource {
  org_id: string;
  org_full_name: string;
  org_street_addr1: string;
  org_state_cd: string;
  org_status_cd: 'A' | 'I';
  org_country_iso_cd: string;
  org_city: string;
  org_zip5: string;
  org_intl_postal_code: string;
  org_province: string;
  ais: ISourceAi[];
  di: ISourceDi;
  [key: string]: any;
}

export interface IAiSearch {
  aiCode: string;
  apEligibilityInd: boolean;
  psatEligibilityInd: boolean;
  satEligibilityInd: boolean;
  clepEligibilityInd: boolean;
  ssdEligibilityInd: boolean;
  stateHomeSchoolInd: boolean;
  aiCategoryCd: number;
  aiStatusCd: number;
  aiLevelCd: number;
}

export interface IDiSearch {
  diCode: string;
  apPgmActiveInd: boolean;
  satPgmActiveInd: boolean;
  //clepPgmActiveInd: boolean;
  satContactNm: string;
  satContactTitle: string;
  satContactEmail: string;
  satContactPhone: string;
  apContactNm: string;
  apContactTitle: string;
  apContactEmail: string;
  apContactPhone: string;
  // clepContactNm: string;
  // clepContactTitle: string;
  // clepContactEmail: string;
  // clepContactPhone: string;
}

export interface IOrgSearch {
  orgId: string;
  name: string;
  streetAddr1: string;
  city: string;
  stateCd: string;
  countryIsoCd: string;
  zip5: string;
  province: string;
  intlPostalCd: string;
  ais?: IAiSearch[]; //orgSearch returns an arrray for ais
  di?: IDiSearch; //orgSearch returns only one di back and not array of dis
  [key: string]: any;
}

export interface IAISearch {
  orgId: string;
  name: string;
  streetAddr1: string;
  city: string;
  stateCd: string;
  countryIsoCd: string;
  zip5: string;
  intlPostalCd: string;
  province: string;
  aiCode: string;
  apEligibilityInd: boolean;
  psatEligibilityInd: boolean;
  satEligibilityInd: boolean;
  clepEligibilityInd: boolean;
  ssdEligibilityInd: boolean;
  stateHomeSchoolInd: boolean;
  aiCategoryCd: number;
  aiStatusCd: number;
  aiLevelCd: number;
}

//TODO: Need to check if we need to return ord info like address in IDISearch
export interface IDISearch {
  orgId: string;
  name: string;
  streetAddr1: string;
  city: string;
  stateCd: string;
  countryIsoCd: string;
  zip5: string;
  intlPostalCd: string;
  province: string;
  diCode: string;
  apPgmActiveInd: boolean;
  satPgmActiveInd: boolean;
  //clepPgmActiveInd: boolean;
  satContactNm: string;
  satContactTitle: string;
  satContactEmail: string;
  satContactPhone: string;
  apContactNm: string;
  apContactTitle: string;
  apContactEmail: string;
  apContactPhone: string;
  // clepContactNm: string;
  // clepContactTitle: string;
  // clepContactEmail: string;
  // clepContactPhone: string;
}

interface IHit {
  _source: ISource;
}

export const OrgService = (refService: IMsReferenceService): any => {
  return {
    search: async (params: ISearchOptions): Promise<IOrgSearch[]> => {
      const data: any = {};
      for (const key of Object.keys(params)) {
        const value = params[key as keyof ISearchOptions];
        if (value === '' || value === undefined) {
          continue;
        }
        data[key] = value;
      }
      return await API.post('AiFormRequest', '/save?key=searchOrg', {
        body: {
          params: {...data}
        }
      }).then((result: any) => {        
        const data: IHit[] = result.hits?.hits || [];
        return data.map((item: IHit) => {
          const source = item._source || {};
          const ais = (source.ais || []).map((ai) => {
            const aiCategoryCd = parseInt(ai.ai_category_cd);
            const aiStatusCd = parseInt(ai.ai_status_cd);
            const aiLevelCd = parseInt(ai.ai_level_cd);

            return {
              aiCode: ai.ai_code,
              apEligibilityInd: ai.ap_eligibility_ind === 'Y' ? true : false,
              psatEligibilityInd: ai.psat_eligibility_ind === 'Y' ? true : false,
              satEligibilityInd: ai.sat_eligibility_ind === 'Y' ? true : false,
              clepEligibilityInd: ai.clep_eligibility_ind === 'Y' ? true : false,
              ssdEligibilityInd: ai.ssd_eligibility_ind === 'Y' ? true : false,
              stateHomeSchoolInd: ai.st_home_sch_rptg_ind === 'Y' ? true : false,
              aiCategoryCd: !isNaN(aiCategoryCd) ? aiCategoryCd : 0,
              aiStatusCd: !isNaN(aiStatusCd) ? aiStatusCd : 0,
              aiLevelCd: !isNaN(aiLevelCd) ? aiLevelCd : 0,
            };
          });
          
          const di =   { 
            diCode: source.di.di_code,
            apPgmActiveInd: source.di.ap_pgm_active_ind === 'Y' ? true : false,
            satPgmActiveInd: source.di.sat_pgm_active_ind === 'Y' ? true : false,
            //clepPgmActiveInd: source.di.clep_pgm_active_ind === 'Y' ? true : false,
            satContactNm: source.di.sat_contact_nm,
            satContactTitle: source.di.sat_contact_title,
            satContactEmail: source.di.sat_contact_email,
            satContactPhone: source.di.sat_contact_phone,
            apContactNm: source.di.ap_contact_nm,
            apContactTitle: source.di.ap_contact_title,
            apContactEmail: source.di.ap_contact_email,
            apContactPhone: source.di.ap_contact_phone,
            // clepContactNm: source.di.clep_contact_nm,
            // clepContactTitle: source.di.clep_contact_title,
            // clepContactEmail: source.di.clep_contact_email,
            // clepContactPhone: source.di.clep_contact_phone,
          };
          return {
            orgId: source.org_id,
            name: source.org_full_name,
            streetAddr1: source.org_street_addr1,
            city: source.org_city,
            stateCd: source.org_state_cd,
            countryIsoCd: source.org_country_iso_cd,
            zip5: source.org_zip5,
            province: source.org_province,
            intlPostalCd: source.org_intl_postal_code,
            ais: ais,
            di:di
          };
        });
      });
    },
    searchAis: async (params: ISearchOptions): Promise<IAISearch[]> => {
      const data: any = {};
      for (const key of Object.keys(params)) {
        const value = params[key as keyof ISearchOptions];
        if (value === '' || value === undefined) {
          continue;
        }
        data[key] = value;
      }
      return await API.post('AiFormRequest', '/save?key=searchAi', {
        body: {
          params: {...data}
        }
      }).then((result: any) => {
        const results: IHit[] = result.hits?.hits || [];
        const data = [];
        for (const item of results) {
          const source = item._source || {};
          const ais = source.ais || [];
          for (const ai of ais) {
            const aiCategoryCd = parseInt(ai.ai_category_cd);
            const aiStatusCd = parseInt(ai.ai_status_cd);
            const aiLevelCd = parseInt(ai.ai_level_cd);
            data.push({
              aiCode: ai.ai_code,
              apEligibilityInd: ai.ap_eligibility_ind === 'Y' ? true : false,
              psatEligibilityInd: ai.psat_eligibility_ind === 'Y' ? true : false,
              satEligibilityInd: ai.sat_eligibility_ind === 'Y' ? true : false,
              clepEligibilityInd: ai.clep_eligibility_ind === 'Y' ? true : false,
              ssdEligibilityInd: ai.ssd_eligibility_ind === 'Y' ? true : false,
              stateHomeSchoolInd: ai.st_home_sch_rptg_ind === 'Y' ? true : false,
              aiCategoryCd: !isNaN(aiCategoryCd) ? aiCategoryCd : 0,
              aiStatusCd: !isNaN(aiStatusCd) ? aiStatusCd : 0,
              aiLevelCd: !isNaN(aiLevelCd) ? aiLevelCd : 0,
              orgId: source.org_id,
              name: source.org_full_name,
              streetAddr1: source.org_street_addr1,
              city: source.org_city,
              stateCd: source.org_state_cd,
              countryIsoCd: source.org_country_iso_cd,
              zip5: source.org_zip5,
              intlPostalCd: source.org_intl_postal_code,
              province: source.org_province
            });
          }
        }
        return data;
      });
    },
    searchDis: async (params: ISearchOptions): Promise<IDISearch[]> => {
      const data: any = {};
      for (const key of Object.keys(params)) {
        const value = params[key as keyof ISearchOptions];
        if (value === '' || value === undefined) {
          continue;
        }
        data[key] = value;
      }
      return await API.post('AiFormRequest', '/save?key=searchDi', {
        body: {
          recordCategoryType: 'di',
          params: {...data}
        }
      }).then((result: any) => {
        const results: IHit[] = result.hits?.hits || [];
        const data = [];
        for (const item of results) {
          const source = item._source || {};
          const di = source.di || {};
          data.push({
            diCode: di.di_code,
            apPgmActiveInd: di.ap_pgm_active_ind === 'Y' ? true : false,
            satPgmActiveInd: di.sat_pgm_active_ind === 'Y' ? true : false,
            //clepPgmActiveInd: di.clep_pgm_active_ind === 'Y' ? true : false,
            satContactNm: di.sat_contact_nm,
            satContactTitle: di.sat_contact_title,
            satContactEmail: di.sat_contact_email,
            satContactPhone: di.sat_contact_phone,
            apContactNm: di.ap_contact_nm,
            apContactTitle: di.ap_contact_title,
            apContactEmail: di.ap_contact_email,
            apContactPhone: di.ap_contact_phone,
            // clepContactNm: di.clep_contact_nm,
            // clepContactTitle: di.clep_contact_title,
            // clepContactEmail: di.clep_contact_email,
            // clepContactPhone: di.clep_contact_phone,
            orgId: source.org_id,
            name: source.org_full_name,
            streetAddr1: source.org_street_addr1,
            city: source.org_city,
            stateCd: source.org_state_cd,
            countryIsoCd: source.org_country_iso_cd,
            zip5: source.org_zip5,
            intlPostalCd: source.org_intl_postal_code,
            province: source.org_province
          });
        }
        return data;
      });
    },
    findAi: async (aiCode: string | undefined, orgId: string | undefined) => {
      if (!aiCode || !orgId)
        throw new Error(`AI Code and Org ID are required for this request - aiCode: '${aiCode}', orgId: '${orgId}'`);

      const response: IAIRecord = await API.post('AiFormRequest', '/save?key=findAi', {
        body: {
          orgId: orgId,
          aiCode: aiCode
        }
      }).then((response: any) => processResponse(response));

      if (response.org?.visitingAddress?.regionCd) {
        const region = await refService.findProvince(response.org?.visitingAddress?.regionCd);
        response.org.visitingAddress.province = region?.label;
      }

      if (response.org?.shippingAddress?.regionCd) {
        const region = await refService.findProvince(response.org?.shippingAddress?.regionCd);
        response.org.shippingAddress.province = region?.label;
      }

      return response;
    },
    findDi: async (orgId: string | undefined) => { //findDi internally gets the associaated diCode for that orgId
      if (!orgId)
        throw new Error(`DI Code and Org ID are required for this request - orgId: '${orgId}'`);

      const response: IDIRecord = await API.post('AiFormRequest', '/save?key=findDi', {
        body: {
          recordCategoryType: 'di',
          orgId: orgId
        }
      }).then((response: any) => processResponse(response));

      if (response.org?.visitingAddress?.regionCd) {
        const region = await refService.findProvince(response.org?.visitingAddress?.regionCd);
        response.org.visitingAddress.province = region?.label;
      }

      return response;
    },
    findOrgById: async (orgId: string) => {
      const response: IOrgRecord = await API.post('AiFormRequest', '/save?key=findOrgById', {
        body: {
          orgId: orgId
        }
      }).then((response: any) => processResponse(response));
      return response;
    }
  };
};